/**
 * «Зарплата → Банковские реестры»: зарплатный реестр в выписке — одна
 * операция на общую сумму; здесь он раскрыт на людей выплатами CRM
 * «ЗП(Банк.Выплаты)». Сверху — итог по месяцам, ниже — реестры (клик
 * раскрывает людей) и выплаты CRM, которые ни в один реестр не попали.
 *
 * Спека: `docs/specs/2026-10-02-payroll-registries-design.md`.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { NoAccess } from "@/components/NoAccess";
import { PAYROLL, useCan } from "@/lib/access";
import {
  type LoosePayout,
  loadRegistries,
  type RegistriesReport,
  type Registry,
  type RegistryMonth,
  type RegistryPayout,
  type RegistryStatus,
  STATUS_HINT,
  STATUS_LABEL,
  type Sum,
} from "@/lib/payroll";
import { money, monthLabel } from "@/lib/pnl";

const TH = "whitespace-nowrap border-b border-border px-2.5 py-2 text-right align-bottom";
const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const NAME = "border-b border-border px-3 py-1 text-left";
const EDGE = "border-l border-border";

const TONE: Record<RegistryStatus, string> = {
  decoded: "border-ok/30 bg-ok-bg text-ok",
  undecoded: "border-err/30 bg-err-bg text-err",
  returned: "border-border bg-bg-subtle text-fg-muted",
};

/** Отбор реестров; `refund` — с возвратом банка в выбранном месяце. */
type Show = "all" | RegistryStatus | "refund";
const SHOWS: { code: Show; label: string }[] = [
  { code: "all", label: "все" },
  { code: "undecoded", label: "не расшифрованы" },
  { code: "decoded", label: "расшифрованы" },
  { code: "returned", label: "возвращены" },
  { code: "refund", label: "с возвратом" },
];

/** Отбор выплат без реестра. */
type PayoutShow = "all" | LoosePayout["status"];
const PAYOUT_LABEL: Record<PayoutShow, string> = {
  all: "все",
  separate: "отдельной операцией",
  missing: "без операции",
};

/** Куда ведёт ячейка итога: отбор реестров и/или выплат без реестра. */
type Pick = { show?: Show; payouts?: PayoutShow };

function day(value: string): string {
  return value.split("-").reverse().join(".");
}

/** Хвост счёта: «…5961». */
function tail(account: string): string {
  return /^\d{20}$/.test(account) ? `…${account.slice(-4)}` : account;
}

function Badge({ status }: { status: RegistryStatus }) {
  return (
    <span title={STATUS_HINT[status]} className={`rounded border px-1.5 py-px text-[11px] ${TONE[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

/** «7 · 1 327 200»; ноль — прочерк. */
function Pair({ sum, tone = "" }: { sum: Sum; tone?: string }) {
  if (!sum.count) return <span className="text-fg-faint">—</span>;
  return (
    <span className={tone}>
      <span className="text-fg-muted">{sum.count} · </span>
      {money(sum.amount, true)}
    </span>
  );
}

function Months({
  report,
  month,
  show,
  payouts,
  onPick,
}: {
  report: RegistriesReport;
  month: string;
  show: Show;
  payouts: PayoutShow;
  onPick: (month: string, pick: Pick) => void;
}) {
  /** Ячейка с числом кликается: ниже остаются только её строки. Месяц `""` — весь период. */
  const cell = (m: string, sum: Sum, pick: Pick, tone = "", edge = false) => {
    const active =
      month !== "" && month === m && show === (pick.show ?? "all") && payouts === (pick.payouts ?? "all");
    return (
      <td
        className={`${CELL} ${edge ? EDGE : ""} ${sum.count ? "cursor-pointer hover:bg-bg-hover" : ""} ${
          active ? "bg-bg-hover outline outline-1 -outline-offset-1 outline-fg/40" : ""
        }`}
        onClick={sum.count ? () => onPick(m, pick) : undefined}
        title={sum.count ? "Показать эти строки" : undefined}
      >
        <Pair sum={sum} tone={tone} />
      </td>
    );
  };
  const row = (m: Omit<RegistryMonth, "month">, key: string, name: string, total = false) => (
    <tr key={name} className={total ? "bg-bg-subtle font-medium" : ""}>
      <td className={`${NAME} whitespace-nowrap`}>{name}</td>
      {cell(key, m.registries, { show: "all" }, "", true)}
      {cell(key, m.decoded, { show: "decoded" }, "text-ok")}
      {cell(key, m.undecoded, { show: "undecoded" }, "text-err")}
      {cell(key, m.refunds, { show: "refund" })}
      {cell(key, m.payouts, { show: "all", payouts: "all" }, "", true)}
      {cell(key, m.in_registry, { show: "decoded" }, "text-ok")}
      {cell(key, m.separate, { payouts: "separate" })}
      {cell(key, m.missing, { payouts: "missing" }, "text-err")}
    </tr>
  );
  return (
    <section data-annot="итог по месяцам" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${NAME} align-bottom`} rowSpan={2}>
              Месяц
            </th>
            <th className={`${TH} ${EDGE} text-center`} colSpan={4}>
              реестры в выписках банков
            </th>
            <th className={`${TH} ${EDGE} text-center`} colSpan={4}>
              выплаты CRM «ЗП(Банк.Выплаты)»
            </th>
          </tr>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${TH} ${EDGE}`}>всего</th>
            <th className={TH} title={STATUS_HINT.decoded}>
              расшифровано
            </th>
            <th className={TH} title={STATUS_HINT.undecoded}>
              не расшифровано
            </th>
            <th className={TH} title="банк вернул деньги по реестру (приход)">
              возвраты
            </th>
            <th className={`${TH} ${EDGE}`}>всего</th>
            <th className={TH} title="выплата вошла в реестр банка — в P&L она через операцию реестра">
              в реестрах
            </th>
            <th className={TH} title="банк провёл выплату своей операцией (Модульбанк платит поштучно)">
              отдельной операцией
            </th>
            <th className={TH} title="ни в реестре, ни отдельной операцией в загруженных выписках выплаты нет">
              без операции
            </th>
          </tr>
        </thead>
        <tbody>
          {report.months.map((m) => row(m, m.month, monthLabel(m.month)))}
          {row(report.total, "", "Итого", true)}
        </tbody>
      </table>
    </section>
  );
}

function People({ rows, faint = false }: { rows: RegistryPayout[]; faint?: boolean }) {
  return (
    <>
      {rows.map((p) => (
        <tr key={p.id} className={`text-[11.5px] ${faint ? "text-fg-muted" : ""}`}>
          <td className={NAME} />
          <td className={`${NAME} whitespace-nowrap`} colSpan={2}>
            {p.user_name ?? "—"}
            {p.role ? <span className="ml-1.5 text-fg-faint">{p.role}</span> : null}
          </td>
          <td className={`${NAME} whitespace-nowrap text-fg-muted`} colSpan={2}>
            {p.location_name ?? "—"}
            {p.recipient_bank ? <span className="ml-1.5 text-fg-faint">банк получателя: {p.recipient_bank}</span> : null}
          </td>
          <td className={`${NAME} whitespace-nowrap tabular-nums text-fg-muted`} title={`выплата CRM №${p.crm_id}`}>
            выплата {day(p.date)}
          </td>
          <td className={CELL} />
          <td className={CELL}>{money(p.amount)}</td>
          <td className={NAME} colSpan={2} />
        </tr>
      ))}
    </>
  );
}

function RegistryRow({ r, open, onToggle }: { r: Registry; open: boolean; onToggle: () => void }) {
  const diff = Number(r.crm_amount) - Number(r.amount);
  return (
    <>
      <tr className="cursor-pointer align-top hover:bg-bg-hover" onClick={onToggle} title={r.purpose}>
        <td className={`${NAME} w-[22px] pr-0 text-fg-faint`}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </td>
        <td className={`${NAME} whitespace-nowrap tabular-nums`}>{day(r.date)}</td>
        <td className={`${NAME} whitespace-nowrap`}>{r.entrepreneur_name}</td>
        <td className={`${NAME} whitespace-nowrap`}>
          {r.bank_name} <span className="text-fg-faint">{tail(r.account)}</span>
        </td>
        <td className={`${NAME} whitespace-nowrap font-mono text-[11px] text-fg-muted`}>{r.label || "—"}</td>
        <td className={`${NAME} whitespace-nowrap text-fg-muted`}>{r.kind_name}</td>
        <td className={CELL}>{money(r.amount)}</td>
        <td className={CELL}>
          {r.payouts.length ? (
            <span title={diff ? `CRM больше банка на ${money(diff)} — округление до рубля` : undefined}>
              {money(r.crm_amount)}
            </span>
          ) : (
            <span className="text-fg-faint">—</span>
          )}
        </td>
        <td className={CELL}>{r.payouts.length || <span className="text-fg-faint">—</span>}</td>
        <td className={NAME}>
          <Badge status={r.status} />
        </td>
      </tr>
      {open ? (
        <>
          <People rows={r.payouts} />
          {r.refunds.map((f) => (
            <tr key={f.id} className="text-[11.5px] text-fg-muted">
              <td className={NAME} />
              <td className={NAME} colSpan={5} title={f.purpose}>
                возврат банка {day(f.date)}
              </td>
              <td className={CELL}>−{money(f.amount)}</td>
              <td className={NAME} colSpan={3} />
            </tr>
          ))}
          {r.status === "undecoded" ? (
            <tr className="text-[11.5px]">
              <td className={NAME} />
              <td className={`${NAME} text-fg-muted`} colSpan={9}>
                {r.same_day.length
                  ? `Выплаты CRM этого дня, не попавшие ни в один реестр (${r.same_day.length}) — среди них может быть часть реестра:`
                  : "В CRM в этот день нет выплат «ЗП(Банк.Выплаты)» без реестра — выплату, скорее всего, не завели."}
              </td>
            </tr>
          ) : null}
          {r.status === "undecoded" ? <People rows={r.same_day} faint /> : null}
          <tr className="text-[11.5px]">
            <td className={NAME} />
            <td className={`${NAME} text-fg-faint`} colSpan={9}>
              {r.purpose} ·{" "}
              <Link
                to={`/pnl/operations?id=${r.id}`}
                className="text-accent-text hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                операция банка
              </Link>{" "}
              · статья {r.article_name}
            </td>
          </tr>
        </>
      ) : null}
    </>
  );
}

function Loose({ rows, filter, onClear }: { rows: LoosePayout[]; filter: string; onClear: () => void }) {
  const missing = rows.filter((p) => p.status === "missing");
  const amount = missing.reduce((s, p) => s + Number(p.amount), 0);
  return (
    <section data-annot="выплаты без реестра" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-bg-subtle px-3 py-2 text-[12px] font-medium">
        Выплаты «ЗП(Банк.Выплаты)» без реестра
        <span className="font-normal text-fg-muted">
          {rows.length
            ? `${rows.length} шт.; без операции в выписках — ${missing.length} на ${money(amount)}`
            : "нет"}
        </span>
        {filter ? (
          <button
            type="button"
            onClick={onClear}
            className="rounded-[var(--radius-ui)] border border-border px-2 py-0.5 font-normal text-fg-muted hover:bg-bg-hover"
            title="Снять отбор"
          >
            {filter} ×
          </button>
        ) : null}
      </div>
      {rows.length ? (
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
              <th className={`${NAME} align-bottom`}>дата</th>
              <th className={`${NAME} align-bottom`}>сотрудник</th>
              <th className={`${NAME} align-bottom`}>город</th>
              <th className={TH}>сумма</th>
              <th className={`${NAME} ${EDGE} align-bottom`}>как ушла</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="align-top">
                <td className={`${NAME} w-[90px] whitespace-nowrap tabular-nums`} title={`выплата CRM №${p.crm_id}`}>
                  {day(p.date)}
                </td>
                <td className={`${NAME} whitespace-nowrap`}>
                  {p.user_name ?? "—"}
                  {p.role ? <span className="ml-1.5 text-fg-faint">{p.role}</span> : null}
                </td>
                <td className={`${NAME} whitespace-nowrap text-fg-muted`}>
                  {p.location_name ?? "—"}
                  {p.recipient_bank ? <span className="ml-1.5 text-fg-faint">{p.recipient_bank}</span> : null}
                </td>
                <td className={CELL}>{money(p.amount)}</td>
                <td className={`${NAME} ${EDGE} text-fg-muted`}>
                  {p.operation ? (
                    <span title={p.operation.purpose}>
                      отдельной операцией: {day(p.operation.date)} · {money(p.operation.amount)} ·{" "}
                      {p.operation.entrepreneur_name}, {p.operation.bank_name}{" "}
                      <span className="text-fg-faint">{tail(p.operation.account)}</span> ·{" "}
                      <Link to={`/pnl/operations?id=${p.operation.id}`} className="text-accent-text hover:underline">
                        операция
                      </Link>
                    </span>
                  ) : (
                    <span className="text-err">операции в выписках нет</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

export function RegistriesPage() {
  return useCan(PAYROLL) ? <RegistriesScreen /> : <NoAccess what="зарплате" />;
}

function RegistriesScreen() {
  const [params, setParams] = useSearchParams();
  const [report, setReport] = useState<RegistriesReport | null>(null);
  const [failure, setFailure] = useState("");
  const [open, setOpen] = useState<Set<number>>(new Set());

  const year = new Date().getFullYear();
  const from = params.get("date_from") ?? `${year}-01-01`;
  const to = params.get("date_to") ?? `${year}-12-31`;
  const show = (params.get("show") as Show | null) ?? "all";
  const month = params.get("month") ?? "";
  const payouts = (params.get("payouts") as PayoutShow | null) ?? "all";
  const registriesRef = useRef<HTMLElement>(null);
  const looseRef = useRef<HTMLDivElement>(null);

  const setParam = (key: string, value: string, empty = "") => {
    const next = new URLSearchParams(params);
    if (value && value !== empty) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!from || !to) return;
    setFailure("");
    setReport(null);
    loadRegistries({ date_from: from, date_to: to })
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [from, to]);

  const inMonth = (day: string) => !month || day.startsWith(month);
  const items = (report?.registries ?? []).filter((r) =>
    show === "refund"
      ? r.refunds.some((f) => inMonth(f.date))
      : (show === "all" || r.status === show) && inMonth(r.date),
  );
  const loose = (report?.payouts ?? []).filter(
    (p) => (payouts === "all" || p.status === payouts) && inMonth(p.date),
  );
  const looseFilter = [payouts === "all" ? "" : PAYOUT_LABEL[payouts], month ? monthLabel(month) : ""]
    .filter(Boolean)
    .join(", ");

  /** Клик по ячейке итога: отбор строк ниже и прокрутка к ним. */
  const pick = (m: string, target: Pick) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of [
      ["month", m],
      ["show", target.show ?? "all"],
      ["payouts", target.payouts ?? "all"],
    ] as const) {
      if (value && value !== "all") next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
    // Нерасшифрованные — сразу раскрытыми: там подсказка, каких выплат дня не хватает.
    if (target.show === "undecoded" && report) {
      const ids = report.registries
        .filter((r) => r.status === "undecoded" && (!m || r.date.startsWith(m)))
        .map((r) => r.id);
      setOpen(new Set(ids));
    }
    const where = target.payouts && target.payouts !== "all" ? looseRef : registriesRef;
    setTimeout(() => where.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };
  const toggle = (id: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-fg-muted">
        <DateRangePicker label="период" from={from} to={to} onChange={(f, t) => setParams(withPeriod(params, f, t))} />
        <span>
          Зарплатный реестр в выписке — одна операция на общую сумму, в P&L он идёт расходом «ФОТ: зарплата». На людей
          его раскладывают выплаты CRM «ЗП(Банк.Выплаты)» рядом по дню, чья сумма совпала с реестром; сами они в P&L
          не идут — расход учтён один раз.
        </span>
      </div>
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {report === null ? (
        failure ? null : <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.registries.length === 0 && report.payouts.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">За период реестров в выписках и выплат «ЗП(Банк.Выплаты)» нет.</div>
      ) : (
        <>
          <Months report={report} month={month} show={show} payouts={payouts} onPick={pick} />

          <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
            {SHOWS.map((s) => (
              <button
                key={s.code}
                type="button"
                onClick={() => setParam("show", s.code, "all")}
                className={`rounded-[var(--radius-ui)] border px-2 py-1 ${
                  show === s.code ? "border-fg bg-bg-hover text-fg" : "border-border text-fg-muted hover:bg-bg-hover"
                }`}
              >
                {s.label}
              </button>
            ))}
            {month ? (
              <button
                type="button"
                onClick={() => setParam("month", "")}
                className="rounded-[var(--radius-ui)] border border-border px-2 py-1 text-fg-muted hover:bg-bg-hover"
                title="Снять отбор месяца"
              >
                {monthLabel(month)} ×
              </button>
            ) : null}
            <span className="ml-1 text-fg-muted">{items.length} шт.</span>
            <button
              type="button"
              onClick={() =>
                setOpen((prev) => (prev.size ? new Set() : new Set(items.map((r) => r.id))))
              }
              className="ml-auto rounded-[var(--radius-ui)] border border-border px-2 py-1 text-fg-muted hover:bg-bg-hover"
            >
              {open.size ? "свернуть все" : "раскрыть все"}
            </button>
          </div>

          <section ref={registriesRef} data-annot="реестры" className="scroll-mt-3 overflow-x-auto rounded-[var(--radius-card)] border border-border">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                  <th className={NAME} />
                  <th className={`${NAME} align-bottom`}>дата</th>
                  <th className={`${NAME} align-bottom`}>ИП-плательщик</th>
                  <th className={`${NAME} align-bottom`}>банк, счёт</th>
                  <th className={`${NAME} align-bottom`}>реестр</th>
                  <th className={`${NAME} align-bottom`}>вид</th>
                  <th className={TH}>сумма банка</th>
                  <th className={TH}>сумма CRM</th>
                  <th className={TH}>людей</th>
                  <th className={`${NAME} align-bottom`}>статус</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <Fragment key={r.id}>
                    <RegistryRow r={r} open={open.has(r.id)} onToggle={() => toggle(r.id)} />
                  </Fragment>
                ))}
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className={`${NAME} py-3 text-fg-muted`}>
                      Под отбор ничего не попало.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </section>

          <div ref={looseRef} className="scroll-mt-3">
            <Loose
              rows={loose}
              filter={looseFilter}
              onClear={() => {
                const next = new URLSearchParams(params);
                next.delete("payouts");
                next.delete("month");
                setParams(next, { replace: true });
              }}
            />
          </div>
        </>
      )}
    </main>
  );
}
